import React, { useState, useEffect, useContext, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  useTheme,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddPrebookingModal from './AddPrebookingModal';
import EditPrebookingModal from './EditPrebookingModal';
import ViewPrebookingModal from './ViewPrebookingModal';
import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

const { PRIVATE_ROUTE } = API_ROUTES;

const TEAL_DARK = '#3DADB5';

const styles = {
  calendar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    width: '100%',
    margin: 'auto',
    gap: '3px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '8px 4px',
    backgroundColor: '#eee',
    fontSize: 'clamp(10px, 1.5vw, 15px)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  cell: {
    minHeight: '100px',
    border: '1px solid #ccc',
    textAlign: 'center',
    backgroundColor: '#fafafa',
    position: 'relative',
    padding: '0 0 1em',
    overflow: 'hidden',
    minWidth: 0,
  },
  empty: {
    backgroundColor: 'transparent',
    border: 'none',
  },
  select: {
    padding: '6px 10px',
    fontSize: '14px',
  },
};

const containerStyle = {
  width: '100%',
  height: 'calc(100vh - 92px)',
  p: { xs: 1, sm: 2, md: 3 },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const formatDate = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export default function PrebookingCalendarView() {
  const { authAxios } = useContext(FetchContext);

  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLandscape = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobile = isMobilePortrait || isMobileLandscape;

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [addModal, setAddModal] = useState({ open: false, date: '' });
  const [viewModal, setViewModal] = useState({ open: false, date: '', records: [] });
  const [editModal, setEditModal] = useState({ open: false, record: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, recordId: null });
  const [records, setRecords] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const fetchPrebookings = useCallback(async () => {
    try {
      const { data } = await authAxios.get(
        `${PRIVATE_ROUTE.GET_PREBOOKING}?year=${year}&month=${month + 1}`
      );
      const list = data?.data ?? [];
      setRecords(list);
      setBookedDates(list.map((r) => (r.date ? r.date.substring(0, 10) : null)).filter(Boolean));
    } catch (e) {
      console.error('Failed to fetch prebookings', e);
    }
  }, [authAxios, year, month]);

  useEffect(() => {
    fetchPrebookings();
  }, [fetchPrebookings]);

  const handleDeleteConfirmed = async () => {
    const { recordId } = confirmDelete;
    setConfirmDelete({ open: false, recordId: null });
    if (!recordId) return;
    try {
      await authAxios.post(`${PRIVATE_ROUTE.DELETE_PREBOOKING}${recordId}`);
      fetchPrebookings();
    } catch (e) {
      console.error('Failed to delete prebooking', e);
    }
  };

  const handleSubmit = () => {
    setYear(selectedYear);
    setMonth(selectedMonth);
  };

  return (
    <>
      <AddPrebookingModal
        open={addModal.open}
        onClose={() => setAddModal({ open: false, date: '' })}
        defaultDate={addModal.date}
        onSuccess={fetchPrebookings}
      />

      <ViewPrebookingModal
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, date: '', records: [] })}
        date={viewModal.date}
        records={viewModal.records}
        onEdit={(rec) => {
          setViewModal({ open: false, date: '', records: [] });
          setEditModal({ open: true, record: rec });
        }}
        onDelete={(rec) => {
          setViewModal({ open: false, date: '', records: [] });
          setConfirmDelete({ open: true, recordId: rec._id });
        }}
      />

      <EditPrebookingModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, record: null })}
        record={editModal.record}
        onSuccess={fetchPrebookings}
      />

      {/* Delete confirmation */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, recordId: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberIcon sx={{ color: '#EF4444' }} />
          Delete Prebooking
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem', color: '#444' }}>
            Are you sure you want to delete this prebooking? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmDelete({ open: false, recordId: null })}
          >
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="prebooking-calendar" sx={containerStyle}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            mb: 2,
            padding: '0.5em',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2a2a2a' }}>
            Prebooking
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              style={styles.select}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {months.map((m, index) => (
                <option key={m} value={index}>{m}</option>
              ))}
            </select>
            <select
              style={styles.select}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>

        {/* Calendar Grid */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={styles.calendar}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} style={styles.header}>{day}</div>
            ))}

            {blanks.map((_, index) => (
              <div
                key={`blank-${index}`}
                style={{
                  ...styles.cell,
                  ...styles.empty,
                  minHeight: isMobile ? '80px' : '130px',
                  height: isMobile ? '80px' : '130px',
                }}
              />
            ))}

            {days.map((day) => {
              const dateKey = formatDate(year, month, day);
              const isToday =
                day === today.getDate() &&
                month === today.getMonth() &&
                year === today.getFullYear();
              const hasBookings = bookedDates.includes(dateKey);

              return (
                <div
                  key={dateKey}
                  style={{
                    ...styles.cell,
                    backgroundColor: isToday ? '#fff5f5' : '#fafafa',
                    height: isMobile ? '80px' : '130px',
                    minHeight: isMobile ? '80px' : '130px',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '3px 5px 2px',
                      gap: '2px',
                    }}
                  >
                    {/* Add button */}
                    <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                      <Tooltip title="Add Prebooking" arrow placement="top">
                        <button
                          type="button"
                          onClick={() => setAddModal({ open: true, date: dateKey })}
                          style={{
                            fontSize: 'clamp(7px, 0.7vw, 9px)',
                            fontWeight: 700,
                            color: '#fff',
                            background: TEAL_DARK,
                            border: 'none',
                            borderRadius: '3px',
                            padding: '1px 4px',
                            cursor: 'pointer',
                            lineHeight: 1.4,
                          }}
                        >
                          +
                        </button>
                      </Tooltip>
                    </div>

                    {/* Date number */}
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: isToday ? '#d32f2f' : 'transparent',
                        color: isToday ? '#fff' : '#333',
                        fontWeight: isToday ? 'bold' : '500',
                        fontSize: 'clamp(10px, 1.1vw, 13px)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        flexShrink: 0,
                        marginLeft: 'auto',
                      }}
                    >
                      {day}
                    </span>
                  </div>

                  {/* View button */}
                  {hasBookings && (
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 4px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setViewModal({
                            open: true,
                            date: dateKey,
                            records: records.filter((r) => r.date?.substring(0, 10) === dateKey),
                          })
                        }
                        style={{
                          fontSize: 'clamp(8px, 0.85vw, 11px)',
                          fontWeight: '700',
                          color: '#fff',
                          background: TEAL_DARK,
                          border: 'none',
                          borderRadius: '4px',
                          padding: '3px 5px',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                      >
                        View
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Box>
    </>
  );
}
